import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Send, ShieldCheck, Loader2 } from "lucide-react";
import { SERVICES, SERVICE_AREAS } from "@/data/business";
import { trackLead } from "@/lib/track";
import { getUtm } from "@/lib/utm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", phone: "", email: "", service: "", city: "", message: "", company: "" };

export default function QuoteForm({ variant = "hero" }) {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, ...getUtm() });
      trackLead("quote_form");
      setForm(initial);
      navigate("/thank-you");
    } catch (err) {
      toast.error("Something went wrong. Please call us at (972) 742-3944.");
    } finally {
      setLoading(false);
    }
  };

  const onHero = variant === "hero";
  const inputClass =
    "w-full rounded-sm border border-slate-300 bg-white px-3.5 py-3 text-slate-900 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-red-600 transition";

  return (
    <div
      id="quote"
      className={`rounded-sm border ${
        onHero ? "border-white/15 bg-white shadow-2xl shadow-black/40" : "border-slate-200 bg-white"
      } p-6 sm:p-7`}
      data-testid="quote-form-card"
    >
      <div className="mb-5">
        <h3 className="font-heading text-2xl font-extrabold text-slate-900">
          Request a Free Quote
        </h3>
        <p className="text-sm text-slate-500 mt-1">
          Fast response — usually within 15 minutes during business hours.
        </p>
      </div>

      <form onSubmit={onSubmit} className="space-y-3" data-testid="quote-form">
        {/* Honeypot — hidden from real users; bots fill it and get silently dropped */}
        <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", overflow: "hidden" }}>
          <label>
            Company
            <input
              type="text"
              name="company"
              tabIndex={-1}
              autoComplete="off"
              value={form.company}
              onChange={onChange}
            />
          </label>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <input
            name="name"
            value={form.name}
            onChange={onChange}
            placeholder="Full name *"
            className={inputClass}
            data-testid="quote-input-name"
          />
          <input
            name="phone"
            value={form.phone}
            onChange={onChange}
            placeholder="Phone number *"
            type="tel"
            className={inputClass}
            data-testid="quote-input-phone"
          />
        </div>
        <input
          name="email"
          value={form.email}
          onChange={onChange}
          placeholder="Email (optional)"
          type="email"
          className={inputClass}
          data-testid="quote-input-email"
        />
        <div className="grid sm:grid-cols-2 gap-3">
          <select
            name="service"
            value={form.service}
            onChange={onChange}
            className={`${inputClass} ${form.service ? "text-slate-900" : "text-slate-400"}`}
            data-testid="quote-select-service"
          >
            <option value="">Service needed</option>
            {SERVICES.map((s) => (
              <option key={s.title} value={s.title}>
                {s.title}
              </option>
            ))}
            <option value="Other">Other / Not sure</option>
          </select>
          <select
            name="city"
            value={form.city}
            onChange={onChange}
            className={`${inputClass} ${form.city ? "text-slate-900" : "text-slate-400"}`}
            data-testid="quote-select-city"
          >
            <option value="">Your city</option>
            {SERVICE_AREAS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <textarea
          name="message"
          value={form.message}
          onChange={onChange}
          placeholder="Briefly describe the problem (optional)"
          rows={3}
          className={inputClass}
          data-testid="quote-input-message"
        />
        <button
          type="submit"
          disabled={loading}
          className="w-full inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-70 active:scale-[0.99] text-white font-bold text-base px-6 py-3.5 rounded-sm transition-colors"
          data-testid="quote-form-submit"
        >
          {loading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" /> Sending...
            </>
          ) : (
            <>
              <Send className="h-4 w-4" /> Get My Free Quote
            </>
          )}
        </button>
        <p className="flex items-center justify-center gap-1.5 text-xs text-slate-400 pt-1">
          <ShieldCheck className="h-3.5 w-3.5 text-red-600" /> No spam. Your info
          is only used to contact you.
        </p>
      </form>
    </div>
  );
}
